// ./src/components/features/Themes/ThemeFormModal/ThemeFormModal.tsx
import React, { useState, useEffect } from "react";
import {
  Grid,
  Stack,
  Alert,
  Text,
  ScrollArea,
  Box,
  TextInput,
  Textarea,
} from "@mantine/core";
import { Theme, ThemeInput } from "@/utils/api";
import { AppModal, AppIcon } from "@/components/generic";
import { defaultThemeConfig, themeEditorUiConfig } from "@/config";
import { ThemeSectionsList } from "./ThemeSectionsList";
import { ThemeSectionEditor, ThemeSectionConfig } from "./ThemeSectionEditor";
import {
  parseThemeConfig,
  setNestedValue,
  stringifyThemeConfig,
} from "./themeConfigUtils";

export interface ThemeFormModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Theme to edit (if null/undefined, it's create mode) */
  theme?: Theme | null;

  /** Callback when theme should be created */
  onCreateTheme?: (themeData: ThemeInput) => Promise<Theme | null>;

  /** Callback when theme should be updated */
  onUpdateTheme?: (
    themeId: string,
    themeData: ThemeInput
  ) => Promise<Theme | null>;

  /** Whether create operation is in progress */
  isCreating?: boolean;

  /** Whether update operation is in progress */
  isUpdating?: boolean;

  /** Last error from theme operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

interface ThemeFormData {
  name: string;
  description: string | null;
  themeConfigData: Record<string, any>;
  status: number;
}

const initialFormData: ThemeFormData = {
  name: "",
  description: null,
  themeConfigData: {},
  status: 100,
};

export const ThemeFormModal: React.FC<ThemeFormModalProps> = ({
  opened,
  onClose,
  theme,
  onCreateTheme,
  onUpdateTheme,
  isCreating = false,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const [formData, setFormData] = useState<ThemeFormData>(initialFormData);
  const [selectedSectionKey, setSelectedSectionKey] =
    useState<string>("global");
  const [validationErrors, setValidationErrors] = useState<any>({});

  const isEditMode = Boolean(theme) && Boolean(theme?.id);
  const isSubmitting = isCreating || isUpdating;

  // Reset form when modal opens/closes or theme changes
  useEffect(() => {
    if (opened) {
      if (theme) {
        // Edit mode - populate form with theme data (create a copy)
        const parsedConfig = parseThemeConfig(theme.themeConfig);
        setFormData({
          name: theme.name || "",
          description: theme.description || null,
          themeConfigData: Object.assign({}, defaultThemeConfig, parsedConfig),
          status: theme.status != null ? theme.status : 100,
        });
      } else {
        // Create mode - use defaults
        const parsedConfig = parseThemeConfig(null);
        setFormData({
          name: "",
          description: null,
          themeConfigData: parsedConfig,
          status: 100,
        });
      }

      // Reset to first section
      setSelectedSectionKey("global");
      setValidationErrors({});
      onClearError?.();
    }
  }, [opened, theme, onClearError]);

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: any = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = "Theme name is required";
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleThemeFieldChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error when user starts typing
    if (validationErrors.name) {
      setValidationErrors((prev: any) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Handle config change for any element
  const handleConfigChange = (path: string, value: string | null) => {
    setFormData((prev) => ({
      ...prev,
      themeConfigData: setNestedValue(prev.themeConfigData, path, value || ""),
    }));
  };

  // Handle save
  const handleSubmit = async () => {
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    try {
      // Stringify config
      const themeConfigString = stringifyThemeConfig(formData.themeConfigData);

      if (!themeConfigString) {
        console.error("Failed to stringify theme config");
        return;
      }

      // Prepare theme data
      const themeData: ThemeInput = {
        name: formData.name.trim(),
        description: formData.description?.trim(),
        themeConfig: themeConfigString,
        status: formData.status,
      };

      //console.log(`Submit'`, themeData);

      let result: Theme | null = null;

      if (isEditMode && theme && onUpdateTheme) {
        // Update existing theme
        result = await onUpdateTheme(theme.id || "", themeData);
      } else if (!isEditMode && onCreateTheme) {
        // Create new theme
        result = await onCreateTheme(themeData);
      }

      // If successful, close the modal
      if (result) {
        onClose();
      }
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Find the selected section config
  const selectedSection = themeEditorUiConfig.find(
    (section) => section.sectionKey === selectedSectionKey
  );

  // Get the error to display for a field
  const getDisplayError = (field: string): string | undefined => {
    return validationErrors[field];
  };

  // Render generic errors
  const renderGenericErrors = () => {
    if (!lastError) {
      return null;
    }

    return (
      <Alert
        color="red"
        icon={<AppIcon iconName="AlertCircle" size={16} />}
        title="Error"
      >
        <Text size="sm">{lastError}</Text>
      </Alert>
    );
  };

  return (
    <AppModal
      opened={opened}
      onClose={handleCancel}
      title={isEditMode ? "Edit Theme" : "Create New Theme"}
      size="xl"
      loading={isSubmitting}
      actions={[
        {
          label: "Cancel",
          variant: "secondary",
          onClick: handleCancel,
          disabled: isSubmitting,
        },
        {
          label: "Save",
          variant: "primary",
          iconName: "Check",
          onClick: handleSubmit,
          inProgress: isSubmitting,
        },
      ]}
    >
      <Stack gap="md">
        {/* Theme Name Input */}
        <TextInput
          label="Theme Name"
          placeholder="Enter theme name"
          value={formData.name}
          onChange={(e) => handleThemeFieldChange("name", e.target.value)}
          error={getDisplayError("name")}
          required
          withAsterisk
          size="md"
        />

        <Textarea
          label="Description"
          placeholder="Enter layout description"
          value={formData.description || ""}
          onChange={(e) =>
            handleThemeFieldChange("description", e.target.value)
          }
          error={getDisplayError("description")}
          rows={2}
          size="md"
        />

        {/* Error display */}
        {renderGenericErrors()}

        {/* Two-column layout: sections list on left, editor on right */}
        <Grid gutter="md">
          {/* Left column: Sections list */}
          <Grid.Col span={{ base: 12, sm: 4, md: 4 }}>
            <Box
              style={(theme) => ({
                borderRight: `1px solid ${theme.colors.gray[3]}`,
                paddingRight: theme.spacing.sm,
                height: "60vh",
                overflowY: "auto",
              })}
            >
              <ThemeSectionsList
                sections={themeEditorUiConfig as ThemeSectionConfig[]}
                selectedSectionKey={selectedSectionKey}
                onSelectSection={setSelectedSectionKey}
              />
            </Box>
          </Grid.Col>

          {/* Right column: Section editor */}
          <Grid.Col span={{ base: 12, sm: 8, md: 8 }}>
            <ScrollArea h="60vh" type="auto">
              <Box pr="md">
                {selectedSection ? (
                  <ThemeSectionEditor
                    sectionConfig={selectedSection as ThemeSectionConfig}
                    themeConfigData={formData.themeConfigData}
                    onConfigChange={handleConfigChange}
                  />
                ) : (
                  <Box p="md">
                    <Text size="sm" c="dimmed">
                      Select a section to configure
                    </Text>
                  </Box>
                )}
              </Box>
            </ScrollArea>
          </Grid.Col>
        </Grid>
      </Stack>
    </AppModal>
  );
};
